import React, { useState, useEffect, useCallback, useMemo, Fragment, useRef } from 'react';
import {
  Printer,
  RefreshCw,
  Search,
  SlidersHorizontal,
  X,
  FileSpreadsheet,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';
import { getPrintHeaderHtml } from '../../services/printHeaderTemplateService';
import {
  FETCH_CHUNK,
  REPORT_TABLE_HEADER_BG,
  REPORT_TABLE_HEADER_TEXT,
  REPORT_TABLE_HEADER_BORDER,
  applyExcelHeaderRowStyle,
  printHtmlInSameTab,
} from './reportPrintHelpers';
import ClientListGrid, { useClientListGrid } from '../ui/ClientListGrid';

function todayInput() {
  return new Date().toISOString().slice(0, 10);
}

function defaultFilters() {
  return {
    dateFrom: todayInput(),
    dateTo: todayInput(),
    isPickupDate: false,
    isCreatedDate: false,
    voucherNo: '',
    billNo: '',
    keyword: '',
    isPaid: false,
    isNotPaid: false,
    statusIds: [],
    areaIds: [],
    clientIds: [],
    /** UI only: id string → display label for selected clients (search may not show them again). */
    clientLabels: {},
  };
}

function idName(x) {
  return {
    id: x.Id ?? x.id,
    name: x.Name ?? x.name ?? '',
  };
}

function formatClientListItem(c) {
  const name = c.Name ?? c.name ?? '';
  const no = c.ClientNo ?? c.clientNo;
  const mobile = c.MobileNo1 ?? c.mobileNo1 ?? '';
  const parts = [name, no != null && no !== '' ? `#${no}` : '', mobile].filter(Boolean);
  return parts.join(' · ') || `Client ${c.Id ?? c.id}`;
}

function normalizeIdArray(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.map((id) => Number(id)).filter((n) => !Number.isNaN(n));
}

function buildDatePartsFromFilter(f) {
  const d0 = new Date(f.dateFrom + 'T12:00:00');
  const d1 = new Date(f.dateTo + 'T12:00:00');
  return {
    FromYear: d0.getFullYear(),
    FromMonth: d0.getMonth() + 1,
    FromDay: d0.getDate(),
    ToYear: d1.getFullYear(),
    ToMonth: d1.getMonth() + 1,
    ToDay: d1.getDate(),
  };
}

function buildPayload(skip, take, f) {
  return {
    Skip: skip,
    Take: take,
    ...buildDatePartsFromFilter(f),
    IsPickupDate: !!f.isPickupDate,
    IsCreatedDate: !!f.isCreatedDate,
    VoucherNo: (f.voucherNo || '').trim(),
    BillNo: (f.billNo || '').trim(),
    Keyword: (f.keyword || '').trim(),
    StatusIds: normalizeIdArray(f.statusIds),
    ClientIds: normalizeIdArray(f.clientIds),
    AreaIds: normalizeIdArray(f.areaIds),
    IsPaid: !!f.isPaid,
    IsNotPaid: !!f.isNotPaid,
    ClientId: 0,
  };
}

/** Legacy Crystal report payload for PrintClientOrders API. */
function buildPrintClientOrdersPayload(f) {
  return {
    ...buildDatePartsFromFilter(f),
    IsPickupDate: !!f.isPickupDate,
    IsCreatedDate: !!f.isCreatedDate,
    VoucherNo: (f.voucherNo || '').trim(),
    BillNo: (f.billNo || '').trim(),
    Keyword: (f.keyword || '').trim(),
    StatusIds: normalizeIdArray(f.statusIds),
    ClientIds: normalizeIdArray(f.clientIds),
    AreaIds: normalizeIdArray(f.areaIds),
    IsPaid: !!f.isPaid,
    IsNotPaid: !!f.isNotPaid,
    ClientId: 0,
  };
}

function parseReportFileName(response) {
  if (response == null) return '';
  if (typeof response === 'string') return response.trim().replace(/^"|"$/g, '');
  return String(response).trim();
}

function formatDate(d) {
  if (d == null || d === '') return '—';
  if (typeof d === 'string') {
    const dt = new Date(d);
    if (!Number.isNaN(dt.getTime())) {
      return dt.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
  }
  if (typeof d === 'object' && d.Day != null && d.Month != null && d.Year != null) {
    const dt = new Date(d.Year, d.Month - 1, d.Day);
    if (!Number.isNaN(dt.getTime())) {
      return dt.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
  }
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function num(n) {
  if (n == null || n === '') return '—';
  const v = typeof n === 'number' ? n : parseFloat(n);
  return Number.isNaN(v) ? '—' : v.toFixed(2);
}

function qtyDisplay(n) {
  if (n == null || n === '') return '—';
  const v = typeof n === 'number' ? n : parseFloat(n);
  if (Number.isNaN(v)) return '—';
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
}

function resolveReportDate(r, f) {
  if (f?.isPickupDate) return formatDate(r.PickupDate ?? r.pickupDate);
  if (f?.isCreatedDate) return formatDate(r.Date ?? r.date);
  return formatDate(r.DeliveryDate ?? r.deliveryDate);
}

function rowToFlat(r, i, filters) {
  const client = r.Client ?? r.client;
  const customer = r.Customer ?? r.customer;
  const status = r.Status ?? r.status;
  const ca = r.ClientAddress ?? r.clientAddress;
  const ce = ca?.Emirate ?? ca?.emirate;
  const cl = ca?.Location ?? ca?.location;
  const custAddr = r.CustomerAddress ?? r.customerAddress;
  const custEm = custAddr?.Emirate ?? custAddr?.emirate;
  const custLoc = custAddr?.Location ?? custAddr?.location;
  const responsible = client?.ResponsibleBy ?? client?.responsibleBy;

  return {
    '#': i + 1,
    VoucherNo: r.VoucherNo ?? r.voucherNo ?? '',
    ClientBillNo: r.ClientBillNo ?? r.clientBillNo ?? '',
    ReportDate: resolveReportDate(r, filters),
    PickupDate: formatDate(r.PickupDate ?? r.pickupDate),
    DeliveryDate: formatDate(r.DeliveryDate ?? r.deliveryDate),
    Quantity: qtyDisplay(r.Quantity ?? r.quantity),
    ProductPrice: r.ProductPrice ?? r.productPrice ?? '',
    DeliveryCharge: r.DeliveryCharge ?? r.deliveryCharge ?? '',
    TAX: r.TAX ?? r.tax ?? '',
    CustomerAmount: r.CustomerAmount ?? r.customerAmount ?? '',
    ClientAmount: r.ClientAmount ?? r.clientAmount ?? '',
    PayType: r.PayType ?? r.payType ?? '',
    ClientEmirate: ce?.Name ?? ce?.name ?? '',
    ClientLocation: cl?.Name ?? cl?.name ?? '',
    Customer: customer?.Name ?? customer?.name ?? '',
    CustomerMobile: customer?.MobileNo1 ?? customer?.mobileNo1 ?? '',
    CustomerEmirate: custEm?.Name ?? custEm?.name ?? '',
    CustomerLocation: custLoc?.Name ?? custLoc?.name ?? '',
    Status: status?.Name ?? status?.name ?? '',
    ResponsibleBy: responsible?.Name ?? responsible?.name ?? '',
    PaymentRefNo: r.PaymentRefNo ?? r.paymentRefNo ?? '',
    ReturnRefNo: r.ReturnRefNo ?? r.returnRefNo ?? '',
  };
}

function rowMatchesTableSearch(r, q) {
  if (!q) return true;
  const o = rowToFlat(r, 0, null);
  const { '#': _n, ...rest } = o;
  const hay = Object.values(rest)
    .map((x) => String(x ?? '').toLowerCase())
    .join(' ');
  return hay.includes(q);
}

/** Group orders by client; preserves row order within each client. Groups sorted A→Z by client name. */
function groupRowsByClient(rows) {
  const map = new Map();
  for (const r of rows) {
    const client = r.Client ?? r.client;
    const id = client?.Id ?? client?.id ?? '_unknown';
    const label = String(client?.Name ?? client?.name ?? 'Unknown client').trim() || 'Unknown client';
    const key = String(id);
    if (!map.has(key)) {
      map.set(key, { key, id, label, client: client || null, rows: [] });
    }
    map.get(key).rows.push(r);
  }
  const groups = [...map.values()];
  groups.sort((a, b) =>
    String(a.label).localeCompare(String(b.label), undefined, { sensitivity: 'base', numeric: true })
  );
  return groups;
}

function formatInputDate(iso) {
  if (!iso) return '—';
  const dt = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(dt.getTime())) return String(iso);
  return dt.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function buildPrintClientInfoHtml(client, esc) {
  if (!client) return '';
  const name = String(client.Name ?? client.name ?? '').trim();
  const mobile = String(client.MobileNo1 ?? client.mobileNo1 ?? '').trim();
  const clientNo = String(client.ClientNo ?? client.clientNo ?? '').trim();
  const company = String(client.Company ?? client.company ?? '').trim();
  const email = String(client.EmailId ?? client.emailId ?? '').trim();
  const tel = String(client.TelNo ?? client.telNo ?? '').trim();

  const items = [];
  if (name) items.push({ label: 'Client Name', value: name });
  if (mobile) items.push({ label: 'Mobile No', value: mobile });
  if (clientNo) items.push({ label: 'Client No', value: clientNo });
  if (company) items.push({ label: 'Company', value: company });
  if (tel) items.push({ label: 'Tel No', value: tel });
  if (email) items.push({ label: 'Email', value: email });
  if (!items.length) return '';

  const rows = items
    .map(
      (item) =>
        `<tr><td class="client-info-label">${esc(item.label)}</td><td class="client-info-value">${esc(item.value)}</td></tr>`
    )
    .join('');
  return `<table class="client-info-table"><tbody>${rows}</tbody></table>`;
}

const PRINT_COLUMN_DEFS = [
  { key: '#', label: 'SL' },
  { key: 'VoucherNo', label: 'Voucher No' },
  { key: 'ReportDate', label: 'Date' },
  { key: 'ClientBillNo', label: 'Client bill' },
  { key: 'PickupDate', label: 'Pickup date' },
  { key: 'DeliveryDate', label: 'Delivery date' },
  { key: 'Quantity', label: 'Qty' },
  { key: 'ProductPrice', label: 'Pro Price' },
  { key: 'DeliveryCharge', label: 'Delivery Charge' },
  { key: 'TAX', label: 'Tax' },
  { key: 'CustomerAmount', label: 'Cust amt' },
  { key: 'ClientAmount', label: 'Amount' },
  { key: 'ClientEmirate', label: 'Client emirate' },
  { key: 'ClientLocation', label: 'Client location' },
  { key: 'Customer', label: 'Customer' },
  { key: 'CustomerMobile', label: 'Mob No' },
  { key: 'CustomerEmirate', label: 'Emirate' },
  { key: 'CustomerLocation', label: 'Cust location' },
  { key: 'PayType', label: 'Paid By' },
  { key: 'Status', label: 'Status' },
  { key: 'ResponsibleBy', label: 'Responsible by' },
  { key: 'PaymentRefNo', label: 'Pay ref' },
  { key: 'ReturnRefNo', label: 'Return ref' },
];

const DEFAULT_PRINT_COLUMN_KEYS = new Set([
  '#',
  'VoucherNo',
  'ReportDate',
  'Customer',
  'CustomerMobile',
  'CustomerEmirate',
  'ProductPrice',
  'DeliveryCharge',
  'ClientAmount',
  'PayType',
  'Status',
]);

const COL_COUNT = PRINT_COLUMN_DEFS.length;

const RIGHT_ALIGN_KEYS = new Set(['ProductPrice', 'DeliveryCharge', 'TAX', 'CustomerAmount', 'ClientAmount']);

const EXTRA_RIGHT_ALIGN = new Set(['Quantity']);

const GRID_COLUMNS = PRINT_COLUMN_DEFS.filter((c) => c.key !== '#').map((c) => ({
  field: c.key,
  title: c.label,
  hideable: true,
  align: RIGHT_ALIGN_KEYS.has(c.key) || EXTRA_RIGHT_ALIGN.has(c.key) ? 'right' : 'left',
}));

function gridCellValue(row, field) {
  const o = rowToFlat(row, 0, null);
  const raw = o[field];
  if (RIGHT_ALIGN_KEYS.has(field)) return num(raw);
  if (raw == null || raw === '') return '—';
  return String(raw);
}

function printCellValue(o, colKey) {
  if (RIGHT_ALIGN_KEYS.has(colKey)) return num(o[colKey]);
  if (colKey === 'Quantity') return qtyDisplay(o[colKey]);
  const raw = o[colKey];
  if (raw == null || raw === '') return '—';
  return String(raw);
}

function isPrintNumericColumn(key) {
  return RIGHT_ALIGN_KEYS.has(key) || EXTRA_RIGHT_ALIGN.has(key);
}

function buildPrintTotalsTableHtml(sums, esc, title) {
  const rows = [
    ['Total Product Price', num(sums.ProductPrice)],
    ['Total Delivery Charge', num(sums.DeliveryCharge)],
    ['Total Client Amount', num(sums.ClientAmount)],
  ];
  const titleRow = title
    ? `<tr><th colspan="2" class="totals-title">${esc(title)}</th></tr>`
    : '';
  const body = rows
    .map(
      ([label, value]) =>
        `<tr><td class="totals-label">${esc(label)}</td><td class="totals-value">${esc(value)}</td></tr>`
    )
    .join('');
  return `<table class="totals-table"><tbody>${titleRow}${body}</tbody></table>`;
}


const SUMMARY_KEYS = ['Quantity', 'ProductPrice', 'DeliveryCharge', 'TAX', 'CustomerAmount', 'ClientAmount'];

function parseNumberField(v) {
  if (v == null || v === '') return 0;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
}

/** Raw sums from order rows (for subtotals / grand total). */
function computeNumericSums(orderRows) {
  const sums = {
    Quantity: 0,
    ProductPrice: 0,
    DeliveryCharge: 0,
    TAX: 0,
    CustomerAmount: 0,
    ClientAmount: 0,
  };
  for (const r of orderRows) {
    sums.Quantity += parseNumberField(r.Quantity ?? r.quantity);
    sums.ProductPrice += parseNumberField(r.ProductPrice ?? r.productPrice);
    sums.DeliveryCharge += parseNumberField(r.DeliveryCharge ?? r.deliveryCharge);
    sums.TAX += parseNumberField(r.TAX ?? r.tax);
    sums.CustomerAmount += parseNumberField(r.CustomerAmount ?? r.customerAmount);
    sums.ClientAmount += parseNumberField(r.ClientAmount ?? r.clientAmount);
  }
  return sums;
}

function formatSumQuantity(n) {
  if (Math.abs(n - Math.round(n)) < 1e-9) return String(Math.round(n));
  return n.toFixed(2);
}

function SummaryTotalsRow({ sums, label, variant = 'group' }) {
  const qtyIdx = PRINT_COLUMN_DEFS.findIndex((c) => c.key === 'Quantity');
  const rowClass =
    variant === 'grand'
      ? 'border-t-2 border-amber-400 bg-amber-100/95 text-amber-950'
      : 'border-b border-amber-200/80 bg-amber-50/95 text-amber-950';
  return (
    <tr className={rowClass}>
      <td
        colSpan={qtyIdx}
        className="px-2 py-2 text-right text-[11px] font-bold uppercase tracking-wide text-amber-900"
      >
        {label}
      </td>
      {PRINT_COLUMN_DEFS.slice(qtyIdx).map((c) => {
        if (SUMMARY_KEYS.includes(c.key)) {
          const v = sums[c.key];
          const display = c.key === 'Quantity' ? formatSumQuantity(v) : num(v);
          return (
            <td key={c.key} className={tdClass(c.key)}>
              {display}
            </td>
          );
        }
        return (
          <td key={c.key} className="border-b border-amber-100/60 px-2 py-1.5 text-gray-200">
            {'\u00a0'}
          </td>
        );
      })}
    </tr>
  );
}

function defaultPrintColumnSelection() {
  return Object.fromEntries(
    PRINT_COLUMN_DEFS.map((d) => [d.key, DEFAULT_PRINT_COLUMN_KEYS.has(d.key)])
  );
}

function filterSummary(f) {
  const parts = [`${f.dateFrom} → ${f.dateTo}`];
  if (f.isPickupDate) parts.push('pickup date');
  else if (f.isCreatedDate) parts.push('created date');
  else parts.push('delivery date');
  if (f.clientIds?.length) {
    const labels = f.clientLabels || {};
    const preview = f.clientIds
      .slice(0, 2)
      .map((id) => labels[String(id)] || `#${id}`)
      .join(', ');
    parts.push(
      `${f.clientIds.length} client(s)${preview ? ` (${preview}${f.clientIds.length > 2 ? '…' : ''})` : ''}`
    );
  } else parts.push('all clients');
  if (f.statusIds?.length) parts.push(`${f.statusIds.length} status(es)`);
  if (f.areaIds?.length) parts.push(`${f.areaIds.length} area(s)`);
  if (f.voucherNo) parts.push(`voucher ${f.voucherNo}`);
  if (f.billNo) parts.push(`bill ${f.billNo}`);
  if (f.keyword) parts.push('keyword');
  if (f.isPaid) parts.push('paid');
  if (f.isNotPaid) parts.push('not paid');
  return parts.join(' · ');
}

function tdClass(key) {
  if (key === '#') return 'px-2 py-2 tabular-nums text-gray-600';
  if (key === 'Quantity') return 'px-2 py-2 text-right tabular-nums';
  if (RIGHT_ALIGN_KEYS.has(key)) return 'px-2 py-2 text-right tabular-nums';
  return 'whitespace-nowrap px-2 py-2';
}

export default function ClientOrderReport() {
  const [appliedFilters, setAppliedFilters] = useState(() => defaultFilters());
  const [draft, setDraft] = useState(() => defaultFilters());
  const [filterModalOpen, setFilterModalOpen] = useState(true);

  const [statuses, setStatuses] = useState([]);
  const [areas, setAreas] = useState([]);
  const [clientPickerSearch, setClientPickerSearch] = useState('');
  const [clientPickerResults, setClientPickerResults] = useState([]);
  const [clientPickerLoading, setClientPickerLoading] = useState(false);
  const clientSearchSeq = useRef(0);
  /** Table: client group keys (g.key) whose detail rows are hidden */
  const [collapsedTableGroupKeys, setCollapsedTableGroupKeys] = useState(() => new Set());
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadProgress, setLoadProgress] = useState(null);
  const [tableSearch, setTableSearch] = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);
  const [excelExporting, setExcelExporting] = useState(false);
  const [printPreparing, setPrintPreparing] = useState(false);
  const [customPrintLoading, setCustomPrintLoading] = useState(false);
  const [printColumnModalOpen, setPrintColumnModalOpen] = useState(false);
  const [printColumnSelection, setPrintColumnSelection] = useState(defaultPrintColumnSelection);
  const [printOrientation, setPrintOrientation] = useState('portrait');
  const [error, setError] = useState('');

  const loadLookups = useCallback(async () => {
    try {
      const [st, ar] = await Promise.all([MainService.StatusInDropDown(), MainService.AreasInDropDown()]);
      setStatuses((Array.isArray(st) ? st : []).map(idName));
      setAreas((Array.isArray(ar) ? ar : []).map(idName));
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    if (filterModalOpen) {
      setDraft({
        ...appliedFilters,
        clientIds: appliedFilters.clientIds || [],
        clientLabels: appliedFilters.clientLabels || {},
      });
      setClientPickerSearch('');
      setClientPickerResults([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterModalOpen]);

  useEffect(() => {
    if (!filterModalOpen) return;
    const q = clientPickerSearch.trim();
    if (q.length < 2) {
      setClientPickerResults([]);
      setClientPickerLoading(false);
      return;
    }
    const seq = ++clientSearchSeq.current;
    const timer = setTimeout(async () => {
      setClientPickerLoading(true);
      try {
        const res = await MainService.ClientsInDropdownList({ Keyword: q, Skip: 0, Take: 100 });
        if (seq !== clientSearchSeq.current) return;
        const raw = res?.Data ?? res?.data ?? res;
        const data = Array.isArray(raw) ? raw : [];
        setClientPickerResults(data);
      } catch (e) {
        console.error(e);
        if (seq === clientSearchSeq.current) setClientPickerResults([]);
      } finally {
        if (seq === clientSearchSeq.current) setClientPickerLoading(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [clientPickerSearch, filterModalOpen]);

  const loadReport = async (f = appliedFilters) => {
    setCollapsedTableGroupKeys(new Set());
    setLoading(true);
    setHasLoaded(false);
    setLoadProgress({ loaded: 0, total: 0 });
    setRows([]);
    setTotal(0);
    setError('');
    try {
      let skip = 0;
      let serverTotal = 0;
      const accumulated = [];
      for (;;) {
        const body = buildPayload(skip, FETCH_CHUNK, f);
        const res = await MainService.ClientOrderReportV2(body);
        const chunk = res?.Data ?? res?.data ?? [];
        const arr = Array.isArray(chunk) ? chunk : [];
        serverTotal = Number(res?.Total ?? res?.total ?? 0) || 0;
        accumulated.push(...arr);
        setRows([...accumulated]);
        setTotal(serverTotal);
        setLoadProgress({ loaded: accumulated.length, total: serverTotal });
        if (arr.length === 0) break;
        skip += arr.length;
        if (skip >= serverTotal) break;
        if (arr.length < FETCH_CHUNK) break;
      }
      setHasLoaded(true);
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Failed to load client order report.');
      setRows([]);
      setTotal(0);
      setHasLoaded(false);
    } finally {
      setLoading(false);
      setLoadProgress(null);
    }
  };

  const applyModalFilters = () => {
    setAppliedFilters({ ...draft });
    setFilterModalOpen(false);
    setTableSearch('');
    loadReport({ ...draft });
  };

  const cancelModal = () => {
    setDraft({ ...appliedFilters });
    setFilterModalOpen(false);
  };

  const openFilterModal = () => setFilterModalOpen(true);

  const onToolbarRefresh = () => {
    if (!hasLoaded || loading) return;
    setTableSearch('');
    loadReport(appliedFilters);
  };

  const clearModalFilters = () => {
    setDraft(defaultFilters());
  };

  const searchQ = tableSearch.trim().toLowerCase();
  const filteredRows = useMemo(() => {
    if (!searchQ) return rows;
    return rows.filter((r) => rowMatchesTableSearch(r, searchQ));
  }, [rows, searchQ]);

  const rowsGroupedByClient = useMemo(() => groupRowsByClient(filteredRows), [filteredRows]);

  const columns = useMemo(() => GRID_COLUMNS, []);
  const grid = useClientListGrid({ columns, rows: filteredRows, getCellValue: gridCellValue });
  const grandNumericSums = useMemo(() => computeNumericSums(grid.processedRows), [grid.processedRows]);

  const toggleTableGroupCollapsed = (groupKey) => {
    setCollapsedTableGroupKeys((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };

  const onExportExcel = async () => {
    if (!hasLoaded || loading) {
      setError('Wait for the report to finish loading, then export.');
      return;
    }
    setExcelExporting(true);
    setError('');
    try {
      if (!filteredRows.length) {
        setError('No rows to export.');
        return;
      }
      const sheetData = rowsGroupedByClient.flatMap((g) =>
        g.rows.map((r, idx) => {
          const flat = rowToFlat(r, idx + 1, appliedFilters);
          return { Client: g.label, ...flat };
        })
      );
      const ws = XLSX.utils.json_to_sheet(sheetData);
      applyExcelHeaderRowStyle(ws);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'ClientOrders');
      const fname = `client-order-report-${appliedFilters.dateFrom}-${appliedFilters.dateTo}.xlsx`;
      XLSX.writeFile(wb, fname);
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Excel export failed.');
    } finally {
      setExcelExporting(false);
    }
  };

  const openPrintColumnModal = () => {
    if (!hasLoaded || loading) {
      setError('Wait for the report to finish loading, then print.');
      return;
    }
    if (!filteredRows.length) {
      setError('No rows to print.');
      return;
    }
    setError('');
    setPrintColumnSelection(defaultPrintColumnSelection());
    setPrintOrientation('portrait');
    setPrintColumnModalOpen(true);
  };

  const onPrintDirect = openPrintColumnModal;

  const onPrintCustom = async () => {
    setCustomPrintLoading(true);
    setError('');
    try {
      const response = await MainService.PrintClientOrders(buildPrintClientOrdersPayload(appliedFilters));
      const fileName = parseReportFileName(response);
      if (!fileName) {
        setError('Print returned no file name.');
        return;
      }
      window.open(`${getApiBaseUrl()}ReportFiles/${fileName}`, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Custom print failed.');
    } finally {
      setCustomPrintLoading(false);
    }
  };

  const executeClientOrderPrint = async () => {
    const activeCols = PRINT_COLUMN_DEFS.filter((d) => printColumnSelection[d.key]);
    if (!activeCols.length) {
      setError('Select at least one column to print.');
      return;
    }

    setPrintColumnModalOpen(false);
    setPrintPreparing(true);
    setError('');
    try {
      if (!filteredRows.length) {
        setError('No rows to print.');
        return;
      }

      const groupsAtPrint = groupRowsByClient(filteredRows);
      const grandSums = computeNumericSums(filteredRows);
      const pageSize = printOrientation === 'landscape' ? 'landscape' : 'portrait';

      const esc = (s) =>
        String(s ?? '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');

      let printHeaderHtml = '';
      try {
        printHeaderHtml = (await getPrintHeaderHtml()) || '';
      } catch (e) {
        console.warn('Client order report print: no print header', e);
      }

      const headHtml = `<tr>${activeCols
        .map((c) => `<th>${esc(c.label)}</th>`)
        .join('')}</tr>`;

      const clientSectionsHtml = groupsAtPrint
        .map((g) => {
          const subSums = computeNumericSums(g.rows);
          const dataRows = g.rows
            .map((r, idx) => {
              const o = rowToFlat(r, idx, appliedFilters);
              const cells = activeCols
                .map((c) => {
                  const cls = isPrintNumericColumn(c.key) ? ' class="num"' : '';
                  return `<td${cls}>${esc(printCellValue(o, c.key))}</td>`;
                })
                .join('');
              return `<tr>${cells}</tr>`;
            })
            .join('');

          const totalsHtml = buildPrintTotalsTableHtml(
            subSums,
            esc,
            `Totals · ${g.rows.length} order${g.rows.length === 1 ? '' : 's'}`
          );
          const clientInfoHtml = buildPrintClientInfoHtml(g.client, esc);

          return `<div class="client-section">
            ${clientInfoHtml}
            <table class="data-table"><thead>${headHtml}</thead><tbody>${dataRows}</tbody></table>
            ${totalsHtml}
          </div>`;
        })
        .join('');

      const grandTotalsHtml =
        groupsAtPrint.length > 1
          ? buildPrintTotalsTableHtml(
              grandSums,
              esc,
              `Grand total · ${filteredRows.length} order${filteredRows.length === 1 ? '' : 's'}`
            )
          : '';

      const dateFromLabel = esc(formatInputDate(appliedFilters.dateFrom));
      const dateToLabel = esc(formatInputDate(appliedFilters.dateTo));
      const headerBlock = printHeaderHtml
        ? `<div class="company-print-header print-repeat-header-cell">${printHeaderHtml}</div>`
        : '';
      const thBg = REPORT_TABLE_HEADER_BG;
      const thColor = REPORT_TABLE_HEADER_TEXT;
      const thBorder = REPORT_TABLE_HEADER_BORDER;
      const htmlDoc = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>&#x200b;</title>
        <style>
          html,body{margin:0;padding:0}
          body{font-family:system-ui,sans-serif;font-size:10px;color:#111}
          .report-print-inner{box-sizing:border-box;padding:12px}
          .company-print-header{margin-bottom:14px}
          .company-print-header img{max-height:72px}
          .report-title{font-size:15px;font-weight:700;margin:0 0 8px;color:#111}
          .statement-dates{display:flex;flex-wrap:wrap;gap:16px 32px;margin:0 0 12px;color:#374151;font-size:11px}
          .statement-dates strong{color:#111;font-weight:600}
          .client-section{margin-bottom:18px}
          table.client-info-table{border-collapse:collapse;width:100%;max-width:520px;margin:0 0 12px;border:1px solid ${thBorder}}
          table.client-info-table td{border:1px solid ${thBorder};padding:4px 8px;vertical-align:top}
          table.client-info-table .client-info-label{width:120px;background:#f9fafb;font-weight:600;color:#374151;white-space:nowrap}
          table.client-info-table .client-info-value{color:#111}
          table.data-table{border-collapse:collapse;width:100%;table-layout:fixed;margin-bottom:8px;page-break-inside:auto}
          table.totals-table{border-collapse:collapse;width:300px;margin:0 0 14px auto;page-break-inside:avoid}
          table.data-table th,table.data-table td{border:1px solid ${thBorder};padding:3px 5px;word-wrap:break-word;overflow-wrap:anywhere;vertical-align:top}
          table.totals-table th,table.totals-table td{border:1px solid ${thBorder};padding:4px 8px}
          table.data-table th{
            background:${thBg} !important;
            color:${thColor};
            font-weight:700;
            -webkit-print-color-adjust:exact;
            print-color-adjust:exact;
          }
          table.data-table td{background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact}
          table.data-table td.num{text-align:right;white-space:nowrap}
          table.data-table tbody tr{page-break-inside:avoid;break-inside:avoid}
          table.totals-table .totals-title{background:#f9fafb;font-weight:700;text-align:left;font-size:10px;color:#111}
          table.totals-table .totals-label{background:#f9fafb;font-weight:600;text-align:left;color:#374151}
          table.totals-table .totals-value{text-align:right;font-weight:700;white-space:nowrap;color:#111}
          .grand-totals-wrap{margin-top:8px;padding-top:10px;border-top:1px solid ${thBorder};page-break-inside:avoid}
          @media print{
            @page{size:${pageSize};margin:10mm}
            html,body{margin:0 !important;padding:0 !important}
            .report-print-inner{
              padding:0 !important;
              box-sizing:border-box;
            }
            thead{display:table-header-group}
            tfoot{display:table-footer-group}
            .company-print-header{page-break-inside:avoid}
          }
        </style></head><body>
        <div class="report-print-inner">
        ${headerBlock}
        <p class="report-title">Client Statement</p>
        <div class="statement-dates">
          <span><strong>Date From:</strong> ${dateFromLabel}</span>
          <span><strong>Date To:</strong> ${dateToLabel}</span>
        </div>
        ${clientSectionsHtml}
        ${grandTotalsHtml ? `<div class="grand-totals-wrap">${grandTotalsHtml}</div>` : ''}
        </div>
        </body></html>`;
      await printHtmlInSameTab(htmlDoc, 'Client statement print');
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Print failed.');
    } finally {
      setPrintPreparing(false);
    }
  };

  const updateDraft = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const toggleDraftClient = (c) => {
    const id = Number(c.Id ?? c.id);
    if (Number.isNaN(id)) return;
    const label = formatClientListItem(c);
    setDraft((d) => {
      const next = new Set((d.clientIds || []).map(Number));
      const labels = { ...(d.clientLabels || {}) };
      if (next.has(id)) {
        next.delete(id);
        delete labels[String(id)];
      } else {
        next.add(id);
        labels[String(id)] = label;
      }
      return { ...d, clientIds: [...next], clientLabels: labels };
    });
  };

  const removeDraftClientId = (id) => {
    setDraft((d) => {
      const labels = { ...(d.clientLabels || {}) };
      delete labels[String(id)];
      return { ...d, clientIds: (d.clientIds || []).filter((x) => Number(x) !== Number(id)), clientLabels: labels };
    });
  };

  return (
    <div className="space-y-4 relative">
      {printColumnModalOpen && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="client-order-report-print-columns-title"
        >
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-gray-200">
              <h3 id="client-order-report-print-columns-title" className="text-lg font-semibold text-gray-900">
                Columns for print / PDF
              </h3>
              <button
                type="button"
                onClick={() => setPrintColumnModalOpen(false)}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="px-5 pt-3 text-sm text-gray-600">
              Choose columns and page orientation for Print Direct (browser print / Save as PDF). Company print header is added at the top automatically.
            </p>
            <div className="px-5 py-3 border-b border-gray-100 space-y-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Page orientation</p>
                <div className="flex flex-wrap gap-4">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
                    <input
                      type="radio"
                      name="client-order-print-orientation"
                      checked={printOrientation === 'portrait'}
                      onChange={() => setPrintOrientation('portrait')}
                      className="border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    Portrait
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
                    <input
                      type="radio"
                      name="client-order-print-orientation"
                      checked={printOrientation === 'landscape'}
                      onChange={() => setPrintOrientation('landscape')}
                      className="border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    Landscape
                  </label>
                </div>
              </div>
            </div>
            <div className="px-5 py-3 flex flex-wrap gap-2 border-b border-gray-100">
              <button
                type="button"
                className="text-xs font-medium text-emerald-700 hover:underline"
                onClick={() => setPrintColumnSelection(defaultPrintColumnSelection())}
              >
                Default columns
              </button>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                className="text-xs font-medium text-emerald-700 hover:underline"
                onClick={() => setPrintColumnSelection(Object.fromEntries(PRINT_COLUMN_DEFS.map((d) => [d.key, true])))}
              >
                Select all
              </button>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                className="text-xs font-medium text-gray-600 hover:underline"
                onClick={() =>
                  setPrintColumnSelection(Object.fromEntries(PRINT_COLUMN_DEFS.map((d) => [d.key, false])))
                }
              >
                Clear all
              </button>
            </div>
            <div className="overflow-y-auto px-5 py-3 space-y-2">
              {PRINT_COLUMN_DEFS.map((col) => (
                <label
                  key={col.key}
                  className="flex items-center gap-3 rounded-lg border border-gray-100 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={!!printColumnSelection[col.key]}
                    onChange={() =>
                      setPrintColumnSelection((prev) => ({
                        ...prev,
                        [col.key]: !prev[col.key],
                      }))
                    }
                    className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-800">{col.label}</span>
                </label>
              ))}
            </div>
            <div className="flex flex-wrap justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50">
              <button
                type="button"
                onClick={() => setPrintColumnModalOpen(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void executeClientOrderPrint()}
                disabled={!PRINT_COLUMN_DEFS.some((d) => printColumnSelection[d.key])}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 disabled:opacity-50"
              >
                <Printer className="w-4 h-4" />
                Print Direct
              </button>
            </div>
          </div>
        </div>
      )}

      {filterModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="client-order-report-filter-title"
        >
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 flex items-center justify-between gap-4 px-5 py-4 border-b border-gray-200 bg-white rounded-t-xl">
              <h3 id="client-order-report-filter-title" className="text-lg font-semibold text-gray-900">
                Client order report filters
              </h3>
              <button
                type="button"
                onClick={cancelModal}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block text-sm">
                  <span className="text-gray-600">From date</span>
                  <input
                    type="date"
                    value={draft.dateFrom}
                    onChange={(e) => updateDraft({ dateFrom: e.target.value })}
                    className="mt-1 w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-gray-600">To date</span>
                  <input
                    type="date"
                    value={draft.dateTo}
                    onChange={(e) => updateDraft({ dateTo: e.target.value })}
                    className="mt-1 w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                  />
                </label>
                <label className="flex items-center gap-2 text-sm sm:col-span-2">
                  <input
                    type="checkbox"
                    checked={draft.isPickupDate}
                    onChange={(e) => updateDraft({ isPickupDate: e.target.checked, isCreatedDate: false })}
                  />
                  Use pickup date for range
                </label>
                <label className="flex items-center gap-2 text-sm sm:col-span-2">
                  <input
                    type="checkbox"
                    checked={draft.isCreatedDate}
                    onChange={(e) => updateDraft({ isCreatedDate: e.target.checked, isPickupDate: false })}
                  />
                  Use created date for range
                </label>
              </div>

              <div className="rounded-lg border border-gray-100 p-3 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <label className="block text-sm font-medium text-gray-700">Clients (optional)</label>
                  <button
                    type="button"
                    className="text-xs text-indigo-600 hover:underline"
                    onClick={() => updateDraft({ clientIds: [], clientLabels: {} })}
                  >
                    Clear all
                  </button>
                </div>

                <div>
                  <label htmlFor="client-order-client-search" className="text-xs text-gray-600">
                    Search clients (min. 2 characters)
                  </label>
                  <div className="relative mt-1">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      id="client-order-client-search"
                      type="search"
                      value={clientPickerSearch}
                      onChange={(e) => setClientPickerSearch(e.target.value)}
                      placeholder="Name, client no, or mobile…"
                      autoComplete="off"
                      className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Up to 100 matches per search. Tick rows to add or remove from the filter.
                  </p>
                </div>

                <div className="max-h-48 overflow-y-auto rounded-md border border-gray-200 bg-white">
                  {clientPickerLoading && (
                    <p className="px-3 py-6 text-center text-sm text-gray-500">Searching…</p>
                  )}
                  {!clientPickerLoading && clientPickerSearch.trim().length < 2 && (
                    <p className="px-3 py-6 text-center text-sm text-gray-500">Type at least 2 characters to search.</p>
                  )}
                  {!clientPickerLoading &&
                    clientPickerSearch.trim().length >= 2 &&
                    clientPickerResults.length === 0 && (
                      <p className="px-3 py-6 text-center text-sm text-gray-500">No clients match this search.</p>
                    )}
                  {!clientPickerLoading &&
                    clientPickerResults.map((c) => {
                      const id = Number(c.Id ?? c.id);
                      const checked = draft.clientIds.map(Number).includes(id);
                      const rowKey = `pick-${id}`;
                      return (
                        <label
                          key={rowKey}
                          className="flex cursor-pointer items-start gap-3 border-b border-gray-100 px-3 py-2.5 last:border-b-0 hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleDraftClient(c)}
                            className="mt-0.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          <span className="text-sm text-gray-900 leading-snug">{formatClientListItem(c)}</span>
                        </label>
                      );
                    })}
                </div>

                {draft.clientIds.length > 0 && (
                  <div>
                    <p className="mb-2 text-xs font-medium text-gray-700">Selected ({draft.clientIds.length})</p>
                    <div className="flex flex-wrap gap-2">
                      {draft.clientIds.map((id) => {
                        const sid = String(id);
                        const lab = (draft.clientLabels && draft.clientLabels[sid]) || `Client #${id}`;
                        return (
                          <span
                            key={sid}
                            className="inline-flex max-w-full items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 pl-2.5 pr-1 py-0.5 text-xs text-emerald-900"
                          >
                            <span className="truncate" title={lab}>
                              {lab}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeDraftClientId(id)}
                              className="shrink-0 rounded-full p-0.5 text-emerald-700 hover:bg-emerald-200/80"
                              aria-label={`Remove ${lab}`}
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <label className="block text-sm font-medium text-gray-700">Status (multi)</label>
                    <button
                      type="button"
                      className="text-xs text-indigo-600 hover:underline"
                      onClick={() => updateDraft({ statusIds: [] })}
                    >
                      Clear
                    </button>
                  </div>
                  <select
                    multiple
                    value={draft.statusIds.map(String)}
                    onChange={(e) => {
                      const v = [...e.target.selectedOptions].map((o) => Number(o.value));
                      updateDraft({ statusIds: v });
                    }}
                    className="mt-1 w-full border border-gray-300 rounded-md px-2 py-1 text-sm min-h-[140px]"
                    size={8}
                  >
                    {statuses.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">{draft.statusIds.length} selected</p>
                </div>
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <label className="block text-sm font-medium text-gray-700">Delivery areas (multi)</label>
                    <button
                      type="button"
                      className="text-xs text-indigo-600 hover:underline"
                      onClick={() => updateDraft({ areaIds: [] })}
                    >
                      Clear
                    </button>
                  </div>
                  <select
                    multiple
                    value={draft.areaIds.map(String)}
                    onChange={(e) => {
                      const v = [...e.target.selectedOptions].map((o) => Number(o.value));
                      updateDraft({ areaIds: v });
                    }}
                    className="mt-1 w-full border border-gray-300 rounded-md px-2 py-1 text-sm min-h-[140px]"
                    size={8}
                  >
                    {areas.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">{draft.areaIds.length} selected</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block text-sm">
                  <span className="text-gray-600">Keyword</span>
                  <input
                    value={draft.keyword}
                    onChange={(e) => updateDraft({ keyword: e.target.value })}
                    className="mt-1 w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                    placeholder="Client / customer / mobile"
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-gray-600">Voucher no</span>
                  <input
                    value={draft.voucherNo}
                    onChange={(e) => updateDraft({ voucherNo: e.target.value })}
                    className="mt-1 w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-gray-600">Bill no</span>
                  <input
                    value={draft.billNo}
                    onChange={(e) => updateDraft({ billNo: e.target.value })}
                    className="mt-1 w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                  />
                </label>
                <div className="flex flex-wrap gap-4 items-center sm:col-span-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={draft.isPaid} onChange={(e) => updateDraft({ isPaid: e.target.checked })} />
                    Paid
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={draft.isNotPaid}
                      onChange={(e) => updateDraft({ isNotPaid: e.target.checked })}
                    />
                    Not paid
                  </label>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 flex flex-wrap justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button
                type="button"
                onClick={clearModalFilters}
                className="px-4 py-2 rounded-lg border border-amber-200 bg-amber-50 text-sm font-medium text-amber-900 hover:bg-amber-100"
              >
                Clear all filters
              </button>
              <button
                type="button"
                onClick={cancelModal}
                className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyModalFilters}
                disabled={loading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
              >
                <Search className="w-4 h-4" />
                Apply filters
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <h2 className="page-title text-2xl font-semibold text-gray-900">Client Orders</h2>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={openFilterModal}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50"
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filters
          </button>
          <button
            type="button"
            onClick={onToolbarRefresh}
            disabled={loading || !hasLoaded}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-40"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={onExportExcel}
            disabled={excelExporting || !hasLoaded || loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-emerald-200 bg-emerald-50 text-sm font-medium text-emerald-900 hover:bg-emerald-100 disabled:opacity-50"
          >
            <FileSpreadsheet className="w-4 h-4" />
            {excelExporting ? 'Exporting…' : 'Excel'}
          </button>
          <button
            type="button"
            onClick={onPrintDirect}
            disabled={printPreparing || !hasLoaded || loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 disabled:opacity-50"
            title="Browser print with column picker and client-wise layout"
          >
            <Printer className="w-4 h-4" />
            {printPreparing ? 'Preparing…' : 'Print Direct'}
          </button>
          <button
            type="button"
            onClick={() => void onPrintCustom()}
            disabled={customPrintLoading || loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-indigo-200 bg-indigo-50 text-sm font-medium text-indigo-900 hover:bg-indigo-100 disabled:opacity-50"
            title="Crystal report PDF from server using current filters"
          >
            <Printer className="w-4 h-4" />
            {customPrintLoading ? 'Printing…' : 'Print Custom'}
          </button>
        </div>
      </div>

      {hasLoaded && (
        <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          Active filters: {filterSummary(appliedFilters)}
        </p>
      )}

      {(hasLoaded || loading) && (
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <div className="relative max-w-xl flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
              disabled={loading && rows.length === 0}
              placeholder="Search loaded rows…"
              className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:bg-gray-50 disabled:text-gray-400"
              aria-label="Search in table"
            />
          </div>
          {rowsGroupedByClient.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-gray-500">Client groups:</span>
              <button
                type="button"
                onClick={() => setCollapsedTableGroupKeys(new Set())}
                className="rounded-md border border-gray-300 bg-white px-2.5 py-1.5 font-medium text-gray-800 hover:bg-gray-50"
              >
                Expand all
              </button>
              <button
                type="button"
                onClick={() => setCollapsedTableGroupKeys(new Set(rowsGroupedByClient.map((g) => g.key)))}
                className="rounded-md border border-gray-300 bg-white px-2.5 py-1.5 font-medium text-gray-800 hover:bg-gray-50"
              >
                Collapse all
              </button>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-4 py-2 text-sm">{error}</div>
      )}

      {!hasLoaded && !loading && (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white px-4 py-10 text-center text-gray-600 text-sm">
          Use <strong>Apply filters</strong> in the dialog to load the report (dialog opens automatically when you open this page).
        </div>
      )}

      <div className="space-y-2">
        {loading && loadProgress != null && (
          <div className="rounded-lg border border-gray-200 bg-white px-3 pb-2 pt-3 shadow-sm">
            <div className="mb-1.5 flex justify-between text-xs text-gray-600">
              <span className="font-medium text-gray-800">Loading pages automatically</span>
              <span className="tabular-nums text-gray-700">
                {loadProgress.total > 0
                  ? `${loadProgress.loaded} / ${loadProgress.total} rows`
                  : rows.length > 0
                    ? `${rows.length} rows…`
                    : 'Connecting…'}
              </span>
            </div>
            {loadProgress.total > 0 ? (
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                <div
                  className="h-full rounded-full bg-emerald-500 transition-[width] duration-300 ease-out"
                  style={{
                    width: `${Math.min(100, (loadProgress.loaded / Math.max(loadProgress.total, 1)) * 100)}%`,
                  }}
                />
              </div>
            ) : (
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                <div className="h-full w-2/5 animate-pulse rounded-full bg-emerald-400" />
              </div>
            )}
          </div>
        )}
        <ClientListGrid
          columns={columns}
          grid={grid}
          loading={loading && rows.length === 0}
          emptyText={rows.length === 0 ? 'No orders for this filter.' : 'No rows match your search.'}
          minWidth="2000px"
          compact
          tableClassName="w-full text-left text-xs"
          theadClassName="sticky top-0 z-10 border-b border-emerald-300 bg-emerald-100"
          maxHeight="75vh"
          footerExtra={hasLoaded && !loading && grid.processedRows.length > 0 ? (
            <SummaryTotalsRow
              sums={grandNumericSums}
              label={`Grand total · ${grid.processedRows.length} order${grid.processedRows.length === 1 ? '' : 's'}`}
              variant="grand"
            />
          ) : null}
          renderCell={(row, column) => row[column.field]}
        />
      </div>
    </div>
  );
}
